import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  fetchWorkspacePatientOptions,
  removePatientOptionImage,
  setWorkspacePatientOptions,
  uploadPatientOptionImage,
  type PatientOptionDraft,
  type PatientOptions,
} from '@/api/patientOptions/patientOptionsApi';
import { WORKSPACE_PATIENT_OPTIONS_QUERY_KEY } from '@/constants/queryKeys';

export function useWorkspacePatientOptionsQuery(
  isEnabled = true
): UseQueryResult<PatientOptions, Error> {
  return useQuery({
    queryKey: WORKSPACE_PATIENT_OPTIONS_QUERY_KEY,
    queryFn: fetchWorkspacePatientOptions,
    enabled: isEnabled,
  });
}

interface SetOptionsVariables {
  category: 'species' | 'warning';
  items: PatientOptionDraft[];
}

export function useSetWorkspacePatientOptionsMutation(): UseMutationResult<
  PatientOptions,
  Error,
  SetOptionsVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function setOptions(variables: SetOptionsVariables) {
      return setWorkspacePatientOptions(variables.category, variables.items);
    },
    onSuccess: function store(options) {
      queryClient.setQueryData(WORKSPACE_PATIENT_OPTIONS_QUERY_KEY, options);
    },
  });
}

interface UploadOptionImageVariables {
  category: 'species' | 'warning';
  code: string;
  file: File;
}

export function useUploadPatientOptionImageMutation(): UseMutationResult<
  PatientOptions,
  Error,
  UploadOptionImageVariables
> {
  return useMutation({
    mutationFn: function upload(variables: UploadOptionImageVariables) {
      return uploadPatientOptionImage(variables.category, variables.code, variables.file);
    },
  });
}

interface RemoveOptionImageVariables {
  category: 'species' | 'warning';
  code: string;
}

export function useRemovePatientOptionImageMutation(): UseMutationResult<
  PatientOptions,
  Error,
  RemoveOptionImageVariables
> {
  return useMutation({
    mutationFn: function remove(variables: RemoveOptionImageVariables) {
      return removePatientOptionImage(variables.category, variables.code);
    },
  });
}
